import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { Plus, Trash2, AlertTriangle, LayoutGrid, CheckSquare, Square } from "lucide-react";

interface Bloqueo {
  id: number;
  titulo: string;
  descripcion?: string;
  tipo: string;
  fechaInicio: string;
  fechaFin: string;
  horaInicio?: string;
  horaFin?: string;
  afectaTodo: boolean;
  zonaIds?: number[];
  mesaNums?: number[];
  estado: string;
  createdAt: string;
}

interface Zona { id: number; nombre: string; orden: number; }
interface SalaConMesas { sala: string; mesas: { id: number; numMesa: number; capacidad: number }[]; }

const TIPOS = [
  { value: "mantenimiento",    label: "🔧 Mantenimiento" },
  { value: "evento",           label: "🎉 Evento especial" },
  { value: "torneo",           label: "🏆 Torneo" },
  { value: "reserva_especial", label: "🎊 Reserva completa de sección" },
  { value: "otro",             label: "📌 Otro motivo" },
];

function hoyISO() { return new Date().toISOString().slice(0, 10); }

const EMPTY = {
  titulo: "", descripcion: "", tipo: "mantenimiento",
  fechaInicio: hoyISO(), fechaFin: hoyISO(),
  horaInicio: "", horaFin: "",
  alcance: "todo" as "todo" | "zonas" | "mesas",
  zonaIds: [] as number[],
  mesaNums: [] as number[],
};

export function BloqueosPage() {
  const { token } = useAuth();
  const [sucursalId, setSucursalId] = useState(1);
  const [sucursales, setSucursales] = useState<{id:number;nombre:string}[]>([]);
  const [bloqueos, setBloqueos] = useState<Bloqueo[]>([]);
  const [zonas, setZonas] = useState<Zona[]>([]);
  const [salas, setSalas] = useState<SalaConMesas[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [salaAbierta, setSalaAbierta] = useState<string | null>(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // Cargar sucursales
  useEffect(() => {
    fetch("/api/v1/sucursales").then(r => r.json())
      .then(d => setSucursales(Array.isArray(d) ? d.map((s: any) => ({ id: s.id, nombre: s.nombre })) : []))
      .catch(() => {});
  }, []);

  const cargar = () => {
    if (!token) return;
    fetch(`/api/v1/bloqueos/sucursal/${sucursalId}`, { headers })
      .then(r => r.json()).then(setBloqueos).catch(() => {});
  };

  const cargarZonasYMesas = () => {
    fetch(`/api/v1/sucursales/${sucursalId}/zonas`)
      .then(r => r.json()).then(setZonas).catch(() => {});
    fetch(`/api/v1/reservas/sucursal-mesas/${sucursalId}`, { headers })
      .then(r => r.json()).then(setSalas).catch(() => {});
  };

  useEffect(() => { cargar(); cargarZonasYMesas(); }, [sucursalId, token]);

  const set = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const toggleZona = (id: number) => {
    setForm(f => ({
      ...f,
      zonaIds: f.zonaIds.includes(id)
        ? f.zonaIds.filter(z => z !== id)
        : [...f.zonaIds, id],
    }));
  };

  const toggleMesa = (num: number) => {
    setForm(f => ({
      ...f,
      mesaNums: f.mesaNums.includes(num)
        ? f.mesaNums.filter(m => m !== num)
        : [...f.mesaNums, num],
    }));
  };

  const seleccionarSala = (sala: SalaConMesas, todo: boolean) => {
    const nums = sala.mesas.map(m => m.numMesa);
    if (todo) {
      setForm(f => ({ ...f, mesaNums: [...new Set([...f.mesaNums, ...nums])] }));
    } else {
      setForm(f => ({ ...f, mesaNums: f.mesaNums.filter(n => !nums.includes(n)) }));
    }
  };

  const guardar = async () => {
    if (!form.titulo.trim()) { setError("El título es obligatorio"); return; }
    if (!form.fechaInicio || !form.fechaFin) { setError("Las fechas son obligatorias"); return; }
    if (form.fechaFin < form.fechaInicio) { setError("La fecha fin no puede ser anterior al inicio"); return; }
    if (form.alcance === "zonas" && form.zonaIds.length === 0) { setError("Selecciona al menos una zona"); return; }
    if (form.alcance === "mesas" && form.mesaNums.length === 0) { setError("Selecciona al menos una mesa o pista"); return; }

    setSaving(true); setError("");
    try {
      const body = {
        titulo: form.titulo,
        descripcion: form.descripcion || null,
        tipo: form.tipo,
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        horaInicio: form.horaInicio || null,
        horaFin: form.horaFin || null,
        afectaTodo: form.alcance === "todo",
        zonaIds: form.alcance === "zonas" ? form.zonaIds : null,
        mesaNums: form.alcance === "mesas" ? form.mesaNums : null,
      };
      const res = await fetch(`/api/v1/bloqueos/sucursal/${sucursalId}`, {
        method: "POST", headers, body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message ?? "Error al guardar");
      }
      setShowForm(false);
      setForm({ ...EMPTY });
      setSalaAbierta(null);
      cargar();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id: number) => {
    if (!confirm("¿Desactivar este bloqueo?")) return;
    await fetch(`/api/v1/bloqueos/${id}`, { method: "DELETE", headers });
    cargar();
  };

  const tipoLabel = (tipo: string) => TIPOS.find(t => t.value === tipo)?.label ?? tipo;
  const colorTipo: Record<string, string> = {
    mantenimiento: "bg-gray-100 text-gray-700",
    evento: "bg-purple-100 text-purple-700",
    torneo: "bg-yellow-100 text-yellow-700",
    reserva_especial: "bg-blue-100 text-blue-700",
    otro: "bg-ink-100 text-ink-600",
  };

  const activos = bloqueos.filter(b => b.estado === "activo");
  const inactivos = bloqueos.filter(b => b.estado !== "activo");

  const totalMesas = salas.reduce((acc, s) => acc + s.mesas.length, 0);

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink-800">Bloqueos</h1>
          <p className="text-ink-500 text-sm mt-1">
            Bloquea salas, mesas o días completos. Los clientes afectados serán notificados.
          </p>
        </div>
        <button onClick={() => { setShowForm(true); setError(""); }}
          className="btn-primary flex items-center gap-2 text-sm">
          <Plus className="size-4" /> Nuevo bloqueo
        </button>
      </div>

      {/* Selector de sucursal dinámico */}
      <div className="flex gap-2 flex-wrap">
        {sucursales.map(s => (
          <button key={s.id} onClick={() => { setSucursalId(s.id); setShowForm(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sucursalId === s.id ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
            {s.nombre}
          </button>
        ))}
      </div>

      {/* Stats rápidos */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="card p-3">
          <p className="text-2xl font-bold text-ink-800">{zonas.length}</p>
          <p className="text-xs text-ink-500">Zonas</p>
        </div>
        <div className="card p-3">
          <p className="text-2xl font-bold text-ink-800">{totalMesas}</p>
          <p className="text-xs text-ink-500">Mesas / Pistas</p>
        </div>
        <div className="card p-3">
          <p className="text-2xl font-bold text-red-600">{activos.length}</p>
          <p className="text-xs text-ink-500">Bloqueos activos</p>
        </div>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="card p-5 space-y-4 border-2 border-brand-200">
          <h3 className="font-semibold text-ink-800">Nuevo bloqueo en {sucursales.find(s => s.id === sucursalId)?.nombre ?? `Sucursal ${sucursalId}`}</h3>

          {/* Título y tipo */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-ink-600 mb-1">Título *</label>
              <input value={form.titulo} onChange={e => set("titulo", e.target.value)}
                className="input w-full" placeholder="Ej: Cierre terraza por evento privado" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Tipo</label>
              <select value={form.tipo} onChange={e => set("tipo", e.target.value)} className="input w-full">
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Descripción (opcional)</label>
              <input value={form.descripcion} onChange={e => set("descripcion", e.target.value)}
                className="input w-full text-sm" placeholder="Detalles para los clientes..." />
            </div>
          </div>

          {/* Fechas y horas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Fecha inicio *</label>
              <input type="date" value={form.fechaInicio} min={hoyISO()}
                onChange={e => set("fechaInicio", e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Fecha fin *</label>
              <input type="date" value={form.fechaFin} min={form.fechaInicio}
                onChange={e => set("fechaFin", e.target.value)} className="input w-full" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">
              Franja horaria <span className="font-normal text-ink-400">(vacío = todo el día)</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <input type="time" value={form.horaInicio}
                onChange={e => set("horaInicio", e.target.value)} className="input w-full" />
              <input type="time" value={form.horaFin}
                onChange={e => set("horaFin", e.target.value)} className="input w-full" />
            </div>
          </div>

          {/* Alcance — ZONA DE SELECCIÓN */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">¿Qué se bloquea?</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { value: "todo",  label: "🔒 Todo", desc: "Toda la sucursal" },
                { value: "zonas", label: "📍 Zonas", desc: `${zonas.length} disponibles` },
                { value: "mesas", label: "🪑 Mesas", desc: `${totalMesas} disponibles` },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => { set("alcance", opt.value); set("zonaIds", []); set("mesaNums", []); }}
                  className={`p-3 rounded-xl border-2 text-sm transition-all ${form.alcance === opt.value ? "border-brand-500 bg-brand-50" : "border-ink-200 hover:border-ink-300"}`}>
                  <p className="font-semibold">{opt.label}</p>
                  <p className="text-xs text-ink-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>

            {/* Selección de Zonas */}
            {form.alcance === "zonas" && (
              <div className="space-y-2">
                <p className="text-xs text-ink-500 font-medium">Selecciona las zonas a bloquear:</p>
                {zonas.length === 0 ? (
                  <p className="text-sm text-ink-400">Esta sucursal no tiene zonas configuradas.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {zonas.map(z => {
                      const sel = form.zonaIds.includes(z.id);
                      return (
                        <button key={z.id} type="button" onClick={() => toggleZona(z.id)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all ${sel ? "border-brand-500 bg-brand-50 text-brand-700 font-medium" : "border-ink-200 text-ink-600 hover:border-ink-300"}`}>
                          {sel ? <CheckSquare className="size-4 text-brand-500" /> : <Square className="size-4 text-ink-300" />}
                          {z.nombre}
                        </button>
                      );
                    })}
                  </div>
                )}
                {form.zonaIds.length > 0 && (
                  <p className="text-xs text-brand-600 font-medium">{form.zonaIds.length} zona(s) seleccionada(s)</p>
                )}
              </div>
            )}

            {/* Selección de Mesas por Sala */}
            {form.alcance === "mesas" && (
              <div className="space-y-3">
                <p className="text-xs text-ink-500 font-medium">Selecciona las mesas o pistas a bloquear:</p>
                {salas.length === 0 ? (
                  <p className="text-sm text-ink-400">No hay mesas cargadas para esta sucursal.</p>
                ) : (
                  salas.map(sala => {
                    const selCount = sala.mesas.filter(m => form.mesaNums.includes(m.numMesa)).length;
                    const todaSel = selCount === sala.mesas.length;
                    const isOpen = salaAbierta === sala.sala;
                    return (
                      <div key={sala.sala} className="border border-ink-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 bg-ink-50">
                          <button type="button" onClick={() => setSalaAbierta(isOpen ? null : sala.sala)}
                            className="flex items-center gap-2 text-sm font-semibold text-ink-700">
                            <LayoutGrid className="size-4 text-ink-400" />
                            {sala.sala}
                            <span className="text-xs font-normal text-ink-400">({sala.mesas.length} mesas)</span>
                            {selCount > 0 && (
                              <span className="px-2 py-0.5 bg-brand-100 text-brand-700 text-xs rounded-full">{selCount} sel.</span>
                            )}
                          </button>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => seleccionarSala(sala, true)}
                              className="text-xs text-brand-600 hover:underline">Todas</button>
                            <button type="button" onClick={() => seleccionarSala(sala, false)}
                              className="text-xs text-ink-400 hover:underline">Ninguna</button>
                          </div>
                        </div>
                        {isOpen && (
                          <div className="p-3 flex flex-wrap gap-2">
                            {sala.mesas.map(m => {
                              const sel = form.mesaNums.includes(m.numMesa);
                              return (
                                <button key={m.id} type="button" onClick={() => toggleMesa(m.numMesa)}
                                  className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all ${sel ? "border-brand-500 bg-brand-500 text-white" : "border-ink-200 text-ink-600 hover:border-ink-400"}`}>
                                  {m.numMesa}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                {form.mesaNums.length > 0 && (
                  <p className="text-xs text-brand-600 font-medium">{form.mesaNums.length} mesa(s) seleccionada(s)</p>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3">
              <AlertTriangle className="size-4 shrink-0" /> {error}
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700 flex items-start gap-2">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              Las reservas <strong>pendientes o confirmadas</strong> en este período serán marcadas como
              <strong>&nbsp;suspendidas</strong> y se notificará por email a los clientes.
            </p>
          </div>

          <div className="flex gap-3">
            <button onClick={() => { setShowForm(false); setError(""); setSalaAbierta(null); }}
              className="btn-secondary flex-1">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="btn-primary flex-1">
              {saving ? "Creando bloqueo..." : "Crear bloqueo"}
            </button>
          </div>
        </div>
      )}

      {/* Lista de bloqueos activos */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-ink-600 uppercase tracking-wide">
          Activos ({activos.length})
        </h3>
        {activos.length === 0 ? (
          <div className="text-center py-8 text-ink-400 text-sm bg-ink-50 rounded-xl">
            Sin bloqueos activos · Presiona "Nuevo bloqueo" para crear uno
          </div>
        ) : (
          activos.map(b => (
            <div key={b.id} className="card p-4 border-l-4 border-red-400">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-ink-800">{b.titulo}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colorTipo[b.tipo] ?? colorTipo.otro}`}>
                      {tipoLabel(b.tipo)}
                    </span>
                    {b.afectaTodo && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Toda la sucursal</span>}
                    {b.zonaIds && b.zonaIds.length > 0 && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {b.zonaIds.length} zona(s)
                      </span>
                    )}
                    {b.mesaNums && b.mesaNums.length > 0 && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        Mesas: {b.mesaNums.join(", ")}
                      </span>
                    )}
                  </div>
                  {b.descripcion && <p className="text-sm text-ink-500 mb-1">{b.descripcion}</p>}
                  <div className="flex items-center gap-3 text-xs text-ink-400 flex-wrap">
                    <span>📅 {b.fechaInicio} → {b.fechaFin}</span>
                    {b.horaInicio && b.horaFin && <span>🕐 {b.horaInicio} - {b.horaFin}</span>}
                  </div>
                </div>
                <button onClick={() => eliminar(b.id)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0">
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {inactivos.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-ink-400 uppercase tracking-wide">Historial ({inactivos.length})</h3>
          {inactivos.map(b => (
            <div key={b.id} className="px-4 py-3 bg-ink-50 rounded-xl opacity-60">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-ink-600 line-through">{b.titulo}</span>
                  <p className="text-xs text-ink-400">{b.fechaInicio} → {b.fechaFin}</p>
                </div>
                <span className="text-xs bg-ink-200 text-ink-500 px-2 py-0.5 rounded-full">Inactivo</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
